import React, { useState, useEffect } from 'react';
import {
  RefreshCw,
  Trash2,
  Download,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Info,
  FileSpreadsheet,
  Database,
  HardDrive,
  HelpCircle,
  X,
  Lock,
  Save,
} from 'lucide-react';
import { api } from '../../utils/api';

/* ------------------------------------------------------------------ */
/*  类型定义                                                            */
/* ------------------------------------------------------------------ */

interface HealthInfo {
  status: string;
  version?: string;
  db?: {
    status: string;
    size?: string;
  };
  storage?: {
    used?: string;
    total?: string;
  };
}

/* ------------------------------------------------------------------ */
/*  系统信息卡片                                                        */
/* ------------------------------------------------------------------ */

interface SystemInfoCardProps {
  health: HealthInfo | null;
  loading: boolean;
  onRefresh: () => void;
}

const SystemInfoCard: React.FC<SystemInfoCardProps> = ({ health, loading, onRefresh }) => {
  const getStatusDisplay = (status: string) => {
    if (status === 'ok' || status === 'healthy') {
      return (
        <span className="inline-flex items-center gap-1.5 text-green-600">
          <CheckCircle2 className="w-4 h-4" />
          <span className="text-sm font-medium">正常</span>
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 text-red-600">
        <XCircle className="w-4 h-4" />
        <span className="text-sm font-medium">异常</span>
      </span>
    );
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Database className="w-5 h-5 text-slate-600" />
          <h3 className="text-base font-semibold text-slate-800">系统信息</h3>
        </div>
        <button
          onClick={onRefresh}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          检查
        </button>
      </div>

      {loading && !health ? (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : health ? (
        <div className="space-y-4">
          {/* 系统状态 */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <p className="text-sm text-slate-500">系统状态</p>
              <p className="text-xs text-slate-400 mt-0.5">ContactConverter 服务运行状态</p>
            </div>
            {getStatusDisplay(health.status)}
          </div>

          {/* 系统版本 */}
          {health.version && (
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="text-sm text-slate-500">系统版本</p>
                <p className="text-xs text-slate-400 mt-0.5">当前运行的版本号</p>
              </div>
              <span className="text-sm font-medium text-slate-700 bg-white px-3 py-1 rounded border border-slate-200">
                v{health.version}
              </span>
            </div>
          )}

          {/* 数据库状态 */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <p className="text-sm text-slate-500">数据库状态</p>
              <p className="text-xs text-slate-400 mt-0.5">
                {health.db?.size ? `D1 数据库 · ${health.db.size}` : 'D1 数据库'}
              </p>
            </div>
            {getStatusDisplay(health.db?.status || 'unknown')}
          </div>

          {/* 存储使用 */}
          {health.storage && (
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2">
                <HardDrive className="w-4 h-4 text-slate-400" />
                <div>
                  <p className="text-sm text-slate-500">存储使用</p>
                  <p className="text-xs text-slate-400 mt-0.5">磁盘空间占用情况</p>
                </div>
              </div>
              <span className="text-sm font-medium text-slate-700">
                {health.storage.used || '-'} / {health.storage.total || '-'}
              </span>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-8 text-slate-400 text-sm">
          点击"检查"按钮获取系统信息
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  数据管理卡片                                                        */
/* ------------------------------------------------------------------ */

interface DataManageCardProps {
  onAction: (action: string) => void;
  loading: boolean;
}

const DataManageCard: React.FC<DataManageCardProps> = ({ onAction, loading }) => {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <div className="flex items-center gap-2 mb-6">
        <Trash2 className="w-5 h-5 text-slate-600" />
        <h3 className="text-base font-semibold text-slate-800">数据管理</h3>
      </div>

      <div className="space-y-3">
        {/* 导出数据库备份 */}
        <button
          onClick={() => onAction('export_backup')}
          disabled={loading}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-slate-200 bg-white text-left hover:bg-slate-50 disabled:opacity-50 transition-colors group"
        >
          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
            <Download className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-700 group-hover:text-blue-600 transition-colors">
              导出数据库备份
            </p>
            <p className="text-xs text-slate-400 mt-0.5">将所有联系人数据导出为 JSON 文件</p>
          </div>
        </button>

        {/* 清空所有导入记录 */}
        <button
          onClick={() => onAction('clear_batches')}
          disabled={loading}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-slate-200 bg-white text-left hover:bg-slate-50 disabled:opacity-50 transition-colors group"
        >
          <div className="w-10 h-10 rounded-lg bg-yellow-50 flex items-center justify-center shrink-0">
            <Trash2 className="w-5 h-5 text-yellow-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-700 group-hover:text-yellow-600 transition-colors">
              清空所有导入记录
            </p>
            <p className="text-xs text-slate-400 mt-0.5">删除所有导入批次记录（不影响联系人数据）</p>
          </div>
        </button>

        {/* 清空所有联系人 */}
        <button
          onClick={() => onAction('clear_contacts')}
          disabled={loading}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-red-200 bg-red-50 text-left hover:bg-red-100 disabled:opacity-50 transition-colors group"
        >
          <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center shrink-0">
            <Trash2 className="w-5 h-5 text-red-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-red-700 group-hover:text-red-800 transition-colors">
              清空所有联系人
            </p>
            <p className="text-xs text-red-400 mt-0.5">删除所有联系人数据，此操作不可撤销</p>
          </div>
        </button>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  使用说明卡片                                                        */
/* ------------------------------------------------------------------ */

const UsageGuideCard: React.FC = () => {
  const supportedFormats = [
    { format: 'XLSX', desc: 'Microsoft Excel 2007+ 格式' },
    { format: 'XLS', desc: 'Microsoft Excel 97-2003 格式' },
    { format: 'CSV', desc: '逗号分隔值文件' },
  ];

  const columnMappings = [
    { column: 'name', label: '姓名', required: true },
    { column: 'phone', label: '电话', required: true },
    { column: 'phone2', label: '备用电话', required: false },
    { column: 'email', label: '邮箱', required: false },
    { column: 'id_card', label: '身份证号', required: false },
    { column: 'vehicle_brand', label: '车辆品牌', required: false },
    { column: 'vehicle_series', label: '车系', required: false },
    { column: 'vehicle_model', label: '车型', required: false },
    { column: 'purchase_date', label: '购车时间', required: false },
    { column: 'company', label: '公司', required: false },
    { column: 'address', label: '地址', required: false },
    { column: 'remark', label: '备注', required: false },
  ];

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <div className="flex items-center gap-2 mb-6">
        <HelpCircle className="w-5 h-5 text-slate-600" />
        <h3 className="text-base font-semibold text-slate-800">使用说明</h3>
      </div>

      <div className="space-y-6">
        {/* 操作步骤 */}
        <div>
          <h4 className="text-sm font-medium text-slate-700 mb-3">操作步骤</h4>
          <ol className="space-y-2">
            {[
              '准备 Excel 或 CSV 文件，确保第一行为列名标题',
              '在首页上传文件，系统将自动解析并导入数据',
              '导入完成后可在"客户管理"中查看和管理联系人',
              '支持将联系人导出为 vCard (.vcf) 格式，可直接导入手机通讯录',
            ].map((step, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-50 text-blue-600 text-xs font-bold shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <span className="text-sm text-slate-600">{step}</span>
              </li>
            ))}
          </ol>
        </div>

        {/* 支持的文件格式 */}
        <div>
          <h4 className="text-sm font-medium text-slate-700 mb-3">支持的文件格式</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {supportedFormats.map(({ format, desc }) => (
              <div
                key={format}
                className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg"
              >
                <FileSpreadsheet className="w-4 h-4 text-emerald-500 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-slate-700">{format}</p>
                  <p className="text-xs text-slate-400">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 列名映射 */}
        <div>
          <h4 className="text-sm font-medium text-slate-700 mb-3">列名映射说明</h4>
          <p className="text-xs text-slate-400 mb-3">
            系统支持中英文列名，以下为标准列名对应关系
          </p>
          <div className="overflow-x-auto border border-slate-200 rounded-lg">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="py-2.5 px-3 text-left font-medium text-slate-500">列名</th>
                  <th className="py-2.5 px-3 text-left font-medium text-slate-500">说明</th>
                  <th className="py-2.5 px-3 text-center font-medium text-slate-500">必填</th>
                </tr>
              </thead>
              <tbody>
                {columnMappings.map(({ column, label, required }) => (
                  <tr key={column} className="border-b border-slate-50 last:border-0">
                    <td className="py-2 px-3">
                      <code className="text-xs bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">
                        {column}
                      </code>
                    </td>
                    <td className="py-2 px-3 text-slate-600">{label}</td>
                    <td className="py-2 px-3 text-center">
                      {required ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-red-50 text-red-600 font-medium">
                          必填
                        </span>
                      ) : (
                        <span className="text-xs text-slate-400">选填</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  经销商访问密码卡片                                                    */
/* ------------------------------------------------------------------ */

const DealerPasswordCard: React.FC = () => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    // 加载当前密码
    const fetchPassword = async () => {
      try {
        const response: any = await api.get('/api/settings?key=dealer_access_password');
        if (response.success && response.data) {
          setPassword(response.data.value);
        }
      } catch {
        // 忽略
      }
    };
    fetchPassword();
  }, []);

  const handleSave = async () => {
    if (!password.trim()) {
      setMessage({ type: 'error', text: '密码不能为空' });
      return;
    }
    setLoading(true);
    setMessage(null);
    try {
      await api.post('/api/settings', { key: 'dealer_access_password', value: password.trim() });
      setMessage({ type: 'success', text: '经销商访问密码已更新' });
    } catch {
      setMessage({ type: 'error', text: '更新失败' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/50">
        <div className="flex items-center gap-2">
          <Lock className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-slate-800">经销商查询访问密码</h2>
        </div>
        <p className="text-sm text-slate-500 mt-1">用户输入此密码可查看经销商完整信息</p>
      </div>
      <div className="p-6">
        {message && (
          <div className={`rounded-lg p-3 mb-4 text-sm ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}>
            {message.text}
          </div>
        )}
        <div className="flex gap-3">
          <input
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="输入经销商访问密码"
          />
          <button
            onClick={handleSave}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
          >
            <Save className="w-4 h-4" />
            {loading ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  SettingsPage 主组件                                                 */
/* ------------------------------------------------------------------ */

const SettingsPage: React.FC = () => {
  const [health, setHealth] = useState<HealthInfo | null>(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // 检查健康状态
  const checkHealth = async () => {
    setHealthLoading(true);
    try {
      const result = await api.get<HealthInfo>('/api/health');
      setHealth(result);
    } catch {
      setHealth({ status: 'error' });
    } finally {
      setHealthLoading(false);
    }
  };

  // 页面加载时自动检查
  useEffect(() => {
    checkHealth();
  }, []);

  // 数据管理操作
  const handleAction = async (action: string) => {
    setActionLoading(true);
    setMessage(null);

    try {
      switch (action) {
        case 'export_backup': {
          const result = await api.post<Blob>('/api/export', { all: true, format: 'json' });
          const blob = result instanceof Blob ? result : new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `contact_backup_${new Date().toISOString().slice(0, 10)}.json`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          setMessage({ type: 'success', text: '数据库备份已导出' });
          break;
        }
        case 'clear_batches': {
          if (!window.confirm('确定要清空所有导入记录吗？此操作不可撤销。')) {
            setActionLoading(false);
            return;
          }
          await api.del('/api/batches', { all: true });
          setMessage({ type: 'success', text: '所有导入记录已清空' });
          break;
        }
        case 'clear_contacts': {
          if (!window.confirm('确定要清空所有联系人数据吗？此操作不可撤销，建议先导出备份。')) {
            setActionLoading(false);
            return;
          }
          await api.del('/api/contacts', { all: true });
          setMessage({ type: 'success', text: '所有联系人数据已清空' });
          break;
        }
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : '操作失败',
      });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* 消息提示 */}
      {message && (
        <div
          className={`rounded-xl p-4 flex items-center gap-2 text-sm border ${
            message.type === 'success'
              ? 'bg-green-50 border-green-200 text-green-600'
              : 'bg-red-50 border-red-200 text-red-600'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle2 className="w-4 h-4 shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 shrink-0" />
          )}
          {message.text}
          <button onClick={() => setMessage(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* 系统信息 */}
      <SystemInfoCard
        health={health}
        loading={healthLoading}
        onRefresh={checkHealth}
      />

      {/* 经销商访问密码 */}
      <DealerPasswordCard />

      {/* 数据管理 */}
      <DataManageCard onAction={handleAction} loading={actionLoading} />

      {/* 使用说明 */}
      <UsageGuideCard />
    </div>
  );
};

export default SettingsPage;
